/** Taking a drawing into the tab — the two ways in, phase 3.
 *
 * ⚠⚠ **THIS IS THE ONLY PLACE THAT DECIDES A DRAWING IS LOCAL.** After it, `api.ts` routes every
 * read and every edit to the browser engine (`registry.ts`), `Studio.tsx` is untouched and does
 * not know, and the server hears nothing more about that drawing.
 *
 * Two doors, and they are genuinely different:
 *
 * * `takeOver(id)` — a drawing the SERVER has. One request for the rows and then silence: the
 *   session's last request. Useful today, and the honest measure of "local mode works" while the
 *   import path is still Python's.
 * * `openFromDisk()` — a .dwg the drafter picked, read by `pddwg` compiled to wasm, with no
 *   request at all. ⚠ Not finished: the engine reads the file to DXF (measured, byte for byte —
 *   `qa/wasm_dwg.py`) and the DXF→rows step is still the server's import. It REFUSES rather than
 *   half-opening, and says which half is missing.
 */
import { LocalDrawing, type OpenSpec } from "./adapter";
import { localEngine } from "./engine";
import { openFile, saveAs, suggestName, writeTo, type PickedFile, type SaveResult }
  from "./files";
import { forgetAllLocal, localFor, localId, registerLocal } from "./registry";
import { csrf, importFile } from "../api";

/** When the server's copy of each locally-held drawing was last written — see `takeOver`. */
const serverStamp = new Map<number, string>();

/** What the studio needs back — the id everything else addresses the drawing by. */
export interface Opened {
  id: number;
  name: string;
  drawing: LocalDrawing;
  /** The local drawings this one displaced — see `forgetAllLocal`. The engine holds ONE
   *  document, so opening a second closes the first, and the studio has to drop its tab or the
   *  drafter is looking at a name that now addresses somebody else's geometry. */
  dropped?: number[];
}

/** Is local mode available at all — is the engine module there? ⚠ Answers rather than throws: a
 *  server with no `pdcad_wasm.wasm` is a normal deployment and the studio simply stays as it is. */
export function localAvailable(): Promise<boolean> {
  return localEngine().available();
}

/** Take over a drawing the server has: fetch its rows once, then work in the tab.
 *
 * ⚠ The version, the table state and the codepage come back as HEADERS, because they are not in
 * the rows and the browser needs all three before it can draw — see `local/base.ts` on why
 * `setup` is not a preference.
 */
export async function takeOver(id: number, name: string): Promise<Opened> {
  const already = localFor(id);
  if (already) return { id, name: already.name, drawing: already };
  const r = await fetch(`/api/drawings/${id}/rows/`, { credentials: "same-origin" });
  if (r.status === 413) {
    // ⚠ The server's own sentence: this drawing is too large to hold in a browser, and it says
    // so in a way a drafter can act on. Repeating it here in different words would be a second
    // explanation of one fact.
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail || "this drawing is too large to work on in the browser");
  }
  if (!r.ok) throw new Error(`the drawing would not come over (${r.status})`);
  // ⚠⚠ **TWO DOCUMENTS, ONE LINE APART** — the display context, then the rows. See the `rows`
  // endpoint: nesting them would re-serialise 30 MB and a second request would be a second
  // query for a few kilobytes. The rows are one line by construction.
  const body = await r.text();
  const cut = body.indexOf("\n");
  if (cut < 0) throw new Error("the drawing arrived without its display context");
  // ⚠⚠ **WHAT THE SERVER'S COPY LOOKED LIKE WHEN THIS TAB TOOK IT.** `saveToServer` sends it
  // back as `since` and the endpoint refuses (409) if anyone has edited the server's copy in
  // between — the only thing standing between a save and silently destroying a colleague's work.
  serverStamp.set(id, r.headers.get("X-CAD-Updated-At") || "");
  const spec: OpenSpec = {
    ctx: body.slice(0, cut),
    rows: body.slice(cut + 1),
    name,
    version: r.headers.get("X-CAD-Version") || "R2013",
    // ⚠ the HEADER says whether the drawing brought its own symbol table; `hasTables` is that,
    // and `base.ts` inverts it into ezdxf's `setup`
    hasTables: (r.headers.get("X-CAD-Tables") || "1") === "1",
    codepage: r.headers.get("X-CAD-Codepage") || "cp1252",
  };
  // ⚠⚠ **BEFORE `LocalDrawing.open`, WHICH IS `store.open` AND REPLACES THE MODULE'S ONE
  // DOCUMENT.** Same reason as `openFromDisk` below — see `forgetAllLocal`. After the fetch, so a
  // drawing that never arrives does not cost the drafter the one they already had.
  const dropped = await forgetAllLocal();
  const d = await LocalDrawing.open(spec);
  registerLocal(id, d);
  return { id, name, drawing: d, dropped };
}

/** What a file opened from disk turns out to hold. */
export interface OpenedFile extends Opened {
  entities: number;
  layers: number;
  spaces: string[];
  ms: number;
}

/** **Open a .dwg (or .dxf) the drafter picked, with no request at all.**
 *
 * ⚠⚠ **ONE ENGINE CALL FROM THE BYTES TO AN OPEN DRAWING.** The reader, the import and the store
 * all live inside the module, so neither the DXF text nor the store's JSON ever crosses
 * `postMessage` — for a 48 MB drawing that would be hundreds of megabytes, twice, of a document
 * the page has no use for. What comes back is a report: the version, whether the file brought its
 * own symbol table, and the counts.
 *
 * ⚠ MUST be called from a user gesture — the file picker is rejected outside one, and the
 * rejection looks exactly like the drafter pressing Cancel.
 *
 * ⚠ `file.bytes` is TRANSFERRED to the worker and detached when this returns.
 */
export async function openFromDisk(picked?: PickedFile | null): Promise<OpenedFile> {
  const file = picked ?? await openFile();
  if (!file) throw new Error("no file was chosen");
  const engine = localEngine();
  // ⚠⚠⚠ **FIRST, AND BEFORE `dwg.open`.** The module holds ONE `STORE`; opening this file
  // replaces it, and any drawing still in the registry would silently start answering out of
  // THIS document. See `forgetAllLocal` for the failure that found it.
  // ⚠ After the picker, not before: a drafter who presses Cancel must keep what they had.
  const dropped = await forgetAllLocal();
  // ⚠ a big drawing leaves the heap big and the allocator keeps it — see `LocalEngine.recycle`
  if (engine.heapBytes() > 1_000_000_000) engine.recycle();
  const t0 = performance.now();
  const dxf = /\.dxf$/i.test(file.name);
  // ⚠ a .dxf is already the text: reading it through the DWG codec would fail on the first byte
  // ⚠⚠⚠ **A DRAWING TOO BIG FOR THE ENGINE TRAPS IN WASM, AND THE TRAP IS UNREADABLE.** The
  // office's real files are 49-96 MB; `ysl-hn-center-2025.dwg` parses natively into a **462 MB**
  // store, and wasm32 has 4 GB of address space in TOTAL. What a drafter saw was
  // `LocalEngineError: dwg.open: Unreachable code should not be executed (evaluating
  // 'this.x.pd_call(n,r,i,a)')` — which is `memory.grow` failing, said in the words of the
  // compiler rather than of the program. ⚠ And the engine is POISONED afterwards: the store is
  // half-built and the next open would read it, so it is recycled before anything else runs.
  // ⚠⚠ **READ THE SIZE BEFORE THE CALL — `callBytes` TRANSFERS THE BUFFER AND DETACHES IT.**
  // The refusal below read `file.bytes.length` afterwards and told the drafter their 49 MB
  // drawing "is 0 MB", which is the kind of number that makes a true message unbelievable.
  const mb = (file.bytes.length / 1e6).toFixed(0);
  let a;
  try {
    a = dxf
      ? await engine.call("dxf.open", [new TextDecoder().decode(file.bytes), file.name])
      : await engine.callBytes("dwg.open", file.bytes.buffer as ArrayBuffer, [file.name]);
  } catch (e) {
    const why = String((e as Error)?.message ?? e);
    engine.recycle();
    if (/unreachable|out of bounds|out of memory|allocation failed|RuntimeError/i.test(why)) {
      throw new Error(
        `${file.name} is ${mb} MB and the drawing engine ran `
        + `out of room reading it. It holds the whole document in one 4 GB address space, and `
        + `a file this size needs more than that. Open it on the server — the same drawing `
        + `works there, where the document does not have to fit in a browser.`);
    }
    throw e;
  }
  if (a.rc !== 0) throw new Error(`${file.name}: ${a.text}`);
  const rep = JSON.parse(a.text) as {
    version: string; tables: boolean; entities: number; layers: number; spaces: string[];
  };
  const id = localId();
  // ⚠ the store is ALREADY open inside the module — `LocalDrawing.attach` does not re-open it,
  // which is the whole reason the document never crossed.
  const d = await LocalDrawing.attach({
    name: file.name, version: rep.version, hasTables: rep.tables, handle: file.handle,
  }, engine);
  registerLocal(id, d);
  return { id, name: file.name, drawing: d, dropped,
           entities: rep.entities, layers: rep.layers,
           spaces: rep.spaces, ms: Math.round(performance.now() - t0) };
}

/** Read a .dwg the drafter picked, in the tab, with no request at all.
 *
 * ⚠ Returns the DXF text and the reader's own report — what it decoded and what it carried,
 * which is the honest half of the answer. ⚠⚠ Turning that DXF into ROWS is still the server's
 * import (`dxfio.import_dxf`), so this is not yet a whole open; it is the half that was hardest
 * and is measured byte for byte against the server in `qa/wasm_dwg.py`.
 *
 * ⚠ `file.bytes` is TRANSFERRED to the worker and is detached when this returns. Read it before,
 * or not at all.
 */
export async function readFromDisk(picked?: PickedFile | null):
    Promise<{ name: string; dxf: string; report: unknown; ms: number }> {
  const file = picked ?? await openFile();
  if (!file) throw new Error("no file was chosen");
  const engine = localEngine();
  // ⚠ a big drawing leaves the heap big and the allocator keeps it — see `LocalEngine.recycle`
  if (engine.heapBytes() > 1_000_000_000) engine.recycle();
  const a = await engine.callBytes("dwg.to_dxf", file.bytes.buffer as ArrayBuffer);
  if (a.rc !== 0) throw new Error(`${file.name}: ${a.text}`);
  const two = JSON.parse(a.text) as [string, string];
  return { name: file.name, dxf: two[0], report: JSON.parse(two[1]), ms: a.ms };
}

/** Save a locally-open drawing to the drafter's own disk — phase 3's other half.
 *
 * ⚠⚠ **THE FILE IS WRITTEN IN THE TAB THAT HOLDS THE DRAWING.** `store.to_dxf` is the engine's
 * own writer (`engine::todxf`), held to `dxfio.export_dxf` over forty drawings by
 * `qa/store_to_dxf_parity.py`; nothing is uploaded, and a drawing that never reached the server
 * can still be handed to a client.
 *
 * ⚠ MUST be called from a user gesture: writing back needs `requestPermission`, and outside a
 * gesture it is denied with no prompt (`files.ts`).
 *
 * ⚠ `copy` forces the picker — "Save a copy". Without it a drawing opened through the File
 * System Access API is written back to the file it came from and anything else is a download,
 * which is a different contract and is reported as one.
 */
export async function saveToDisk(id: number, copy = false):
    Promise<{ how: SaveResult["how"]; name: string; bytes: number; ms: number }> {
  const d = localFor(id);
  if (!d) throw new Error("that drawing is not open in this tab");
  const t0 = performance.now();
  const text = await d.toDxf();
  const bytes = new TextEncoder().encode(text);
  const name = suggestName(d.name, "dxf");
  const handle = d.handle as (FileSystemFileHandle & { name: string }) | null;
  // ⚠ write back ONLY when the file we would overwrite is the one we would have written anyway:
  // a .dwg opened from disk becomes a .dxf, and silently replacing the client's .dwg with DXF
  // bytes under a .dwg name is the one thing a CAD app must never do (`files.ts`, `suggestName`).
  const sameFile = !copy && handle != null && handle.name === name;
  const got = sameFile
    ? await writeTo(handle, bytes)
    : await saveAs(bytes, name);
  return { how: got.how, name: got.name, bytes: bytes.length,
           ms: Math.round(performance.now() - t0) };
}

/** Save a locally-open drawing to the drafter's own disk AS A .dwg — the file they handed over,
 * given back as itself.
 *
 * ⚠⚠ **A DXF IS NOT A DWG TO THE PERSON RECEIVING IT.** Every reader takes a DXF, and that is why
 * `saveToDisk` was enough to call phase 3 done — but a client who sends a .dwg and gets a .dxf
 * back has to explain it to whoever they forward it to, and a drafter who opened a file expects
 * to save it. `pdcad_core::todwg` writes the DWG here, in the tab that holds the drawing, and the
 * ODA File Converter opens what it makes (`qa/rust_dwgwrite.py`).
 *
 * ⚠⚠ **WHAT THE WRITER CANNOT BUILD YET IS RETURNED, NOT HIDDEN.** 99% of a 19 MB client drawing
 * survives; the rest is counted by kind, and the caller is expected to say so. A save that
 * quietly drops a wall's worth of ink is worse than one that refuses.
 *
 * ⚠ MUST be called from a user gesture, for the same reason `saveToDisk` must.
 */
export async function saveDwgToDisk(id: number, copy = false):
    Promise<{ how: SaveResult["how"]; name: string; bytes: number; ms: number; report: unknown }> {
  const d = localFor(id);
  if (!d) throw new Error("that drawing is not open in this tab");
  const t0 = performance.now();
  const { report, bytes } = await d.toDwg();
  const name = suggestName(d.name, "dwg");
  const handle = d.handle as (FileSystemFileHandle & { name: string }) | null;
  // ⚠ the same rule as the DXF save, and here it is the EASY case: a .dwg opened from disk is
  // written back as a .dwg, so the names agree and the drafter gets their own file updated.
  const sameFile = !copy && handle != null && handle.name === name;
  const got = sameFile ? await writeTo(handle, bytes) : await saveAs(bytes, name);
  return { how: got.how, name: got.name, bytes: bytes.length, report,
           ms: Math.round(performance.now() - t0) };
}

/** Put a locally-open drawing INTO THE OFFICE SYSTEM — phase 5's storage, in one command.
 *
 * ⚠⚠ **A DRAWING EDITED IN THIS TAB HAD NOWHERE TO GO BUT THE DRAFTER'S OWN DISK.** They could
 * save it and then use Import, which is two steps through a file nobody wanted — and on the
 * desktop app the file is the only copy there is. This writes the .dwg in the tab
 * (`pdcad_core::todwg`) and posts it to the import endpoint a drafter already uses, so the
 * drawing lands in the list with its layers, its blocks and its sheets.
 *
 * ⚠⚠ **IT MAKES A NEW DRAWING EVERY TIME, exactly as Import does**, because that is what the
 * endpoint means. Called twice it leaves two, and the caller is expected to say so rather than
 * let a drafter discover it in the list. Updating one in place is a different contract — it would
 * have to reconcile edits the server made in the meantime — and is not this.
 *
 * ⚠ There is no server in the desktop app, so this cannot run there; `standalone()` is the test
 * and the caller makes it.
 */
export async function uploadToServer(id: number):
    Promise<{ id: number; name: string; bytes: number; report: unknown; ms: number }> {
  const d = localFor(id);
  if (!d) throw new Error("that drawing is not open in this tab");
  const t0 = performance.now();
  const { report, bytes } = await d.toDwg();
  const name = suggestName(d.name, "dwg");
  // ⚠ a `File` rather than a `Blob`: the endpoint reads `up.name` for the drawing's name and the
  // extension decides which reader it uses
  const f = new File([bytes as unknown as BlobPart], name, { type: "application/acad" });
  const got = await importFile(f);
  const made = got.drawing as { id?: number; name?: string } | undefined;
  return { id: Number(made?.id ?? 0), name: String(made?.name ?? name), bytes: bytes.length,
           report, ms: Math.round(performance.now() - t0) };
}

/** **SAVE — the drawing this tab has been editing, back onto the one it was opened from.**
 *
 * ⚠⚠⚠ **THIS IS WHAT `uploadToServer` COULD NOT DO, AND WHY LOCAL MODE COULD NOT BE A DEFAULT.**
 * `uploadToServer` posts to the IMPORT endpoint, which makes a NEW drawing every time: a drafter
 * who opened *Plan A* and pressed Save got *Plan A (1)* in the list beside the one they meant to
 * update. This posts to `replace_file`, which swaps the rows onto the same id — same drawing,
 * same name, same place in the list, same address for anything that links to it.
 *
 * ⚠⚠ **`since` IS THE WHOLE SAFETY OF IT.** This tab has been holding its own copy with the
 * server hearing nothing; if anyone edited the server's in the meantime, an unconditional save
 * would destroy their work with no undo that reaches across it. The stamp comes from
 * `X-CAD-Updated-At` at take-over and the server answers **409** if it has moved. The caller is
 * expected to offer "save a copy" then, not to retry with `force`.
 * ⚠ `force` exists for the drafter who has been told and means it. It is never the default.
 *
 * ⚠ MUST be called from a user gesture for the same reason the disk saves must — and because a
 * save that a drafter did not ask for is the one thing worse than no save at all.
 */
export async function saveToServer(id: number, force = false):
    Promise<{ id: number; name: string; bytes: number; report: unknown; ms: number }> {
  const d = localFor(id);
  if (!d) throw new Error("that drawing is not open in this tab");
  if (id < 0) throw new Error("this drawing came from a file, not from the server — use Save As");
  const t0 = performance.now();
  const { report, bytes } = await d.toDwg();
  const name = suggestName(d.name, "dwg");
  const form = new FormData();
  form.append("file", new File([bytes as unknown as BlobPart], name,
                               { type: "application/acad" }));
  const since = serverStamp.get(id) || "";
  if (since && !force) form.append("since", since);
  if (force) form.append("force", "1");
  const r = await fetch(`/api/drawings/${id}/replace_file/`, {
    method: "POST", credentials: "same-origin", headers: { Accept: "application/json", ...csrf() },
    body: form,
  });
  const got = await r.json().catch(() => ({}));
  if (r.status === 409) {
    // ⚠ The server's own sentence — it names the drawing and when it changed, which is what a
    // drafter needs to decide. Wrapping it in "conflict" would throw that away.
    const e = new Error(String(got?.detail || "somebody else has saved this drawing"));
    (e as Error & { conflict?: boolean }).conflict = true;
    throw e;
  }
  if (!r.ok) throw new Error(String(got?.detail || `save failed (${r.status})`));
  const made = got.drawing as { id?: number; name?: string; updated_at?: string } | undefined;
  // ⚠ The drawing is still local and still being edited — re-stamp it, or the NEXT save conflicts
  // with the one this call just made.
  if (made?.updated_at) serverStamp.set(id, made.updated_at);
  return { id: Number(made?.id ?? id), name: String(made?.name ?? d.name),
           bytes: bytes.length, report, ms: Math.round(performance.now() - t0) };
}

/** An id for a drawing with no server id. */
export { localId };
